import { useState } from "react";
import { BrandLogo } from "@/components/BrandLogo";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, LogOut, LayoutDashboard, Search, Shield } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useSite } from "@/context/SiteContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { CartDrawer } from "@/components/CartDrawer";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";

const DEFAULT_LINKS = [{ to: "/", label: "Home" }];
const tid = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");

const NavLink = ({ l, active, t, mobile, onClick }) => {
  const cls = mobile
    ? "py-3 text-neutral-700 hover:text-[#d00000] border-b border-neutral-100"
    : `text-sm tracking-wide transition-colors duration-200 ${active ? "text-neutral-900 font-semibold" : "text-neutral-500 hover:text-[#d00000]"}`;
  const id = `${mobile ? "mobile-nav" : "nav"}-${tid(l.label)}`;
  return /^https?:/.test(l.to)
    ? <a href={l.to} target="_blank" rel="noreferrer" className={cls} data-testid={id}>{t(l.label)}</a>
    : <Link to={l.to} onClick={onClick} className={cls} data-testid={id}>{t(l.label)}</Link>;
};

export const Navbar = () => {
  const { user, logout } = useAuth();
  const { config, t } = useSite();
  const nav = config?.navigation || {};
  const links = nav.header_links?.length ? nav.header_links : DEFAULT_LINKS;
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [query, setQuery] = useState("");
  const loginLabel = t(nav.login_label || "Login");

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  const submitSearch = (e) => {
    e.preventDefault();
    setSearchOpen(false);
    navigate(`/marketplace${query ? `?q=${encodeURIComponent(query)}` : ""}`);
    setQuery("");
  };

  return (
    <header className="bg-white/90 backdrop-blur-xl border-b border-neutral-200" data-testid="navbar">
      <div className="max-w-7xl mx-auto px-5 sm:px-8 h-16 flex items-center justify-between">
        <BrandLogo className="h-10" testId="logo-link" />

        <nav className="hidden md:flex items-center gap-7">
          {links.map((l) => <NavLink key={l.to + l.label} l={l} t={t} active={location.pathname === l.to} />)}
        </nav>

        <div className="flex items-center gap-1">
          <LanguageSwitcher />
          {nav.show_search !== false && (
            <button onClick={() => setSearchOpen((v) => !v)} data-testid="search-toggle" aria-label="Search"
              className="w-10 h-10 flex items-center justify-center text-neutral-900 hover:text-[#d00000] transition-colors">
              <Search className="w-5 h-5" />
            </button>
          )}
          {nav.show_cart !== false && <CartDrawer />}

          <div className="hidden md:flex items-center gap-2 ml-1">
            {user ? (
              <>
                {user.is_admin && (
                  <Link to="/admin" data-testid="nav-admin">
                    <Button variant="ghost" className="text-neutral-900 hover:bg-neutral-100 rounded-none gap-2">
                      <Shield className="w-4 h-4" /> {t("Admin")}
                    </Button>
                  </Link>
                )}
                <Link to="/dashboard" data-testid="nav-dashboard">
                  <Button variant="ghost" className="text-neutral-900 hover:bg-neutral-100 rounded-none gap-2">
                    <LayoutDashboard className="w-4 h-4" /> {user.name?.split(" ")[0]}
                  </Button>
                </Link>
                <Button onClick={handleLogout} data-testid="logout-btn" variant="ghost" className="text-neutral-500 hover:text-[#d00000] hover:bg-neutral-100 rounded-none">
                  <LogOut className="w-4 h-4" />
                </Button>
              </>
            ) : (
              <Link to="/login" data-testid="nav-login">
                <Button className="bg-[#d00000] hover:bg-[#a30000] text-white rounded-none">{loginLabel}</Button>
              </Link>
            )}
          </div>

          <button className="md:hidden text-neutral-900 ml-1" onClick={() => setOpen(!open)} data-testid="mobile-menu-toggle" aria-label="Menu">
            {open ? <X /> : <Menu />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {searchOpen && (
          <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }}
            className="overflow-hidden bg-white border-t border-neutral-200">
            <form onSubmit={submitSearch} className="max-w-7xl mx-auto px-5 sm:px-8 py-4 flex gap-3" data-testid="header-search-form">
              <div className="relative flex-1">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-neutral-400" />
                <Input autoFocus value={query} onChange={(e) => setQuery(e.target.value)} placeholder={t("Search dishes, cuisines…")} data-testid="header-search-input"
                  className="h-12 pl-12 bg-white border-neutral-300 rounded-none focus-visible:ring-[#d00000]" />
              </div>
              <Button type="submit" data-testid="header-search-submit" className="h-12 px-6 bg-[#d00000] hover:bg-[#a30000] text-white rounded-none">{t("Search")}</Button>
            </form>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {open && (
          <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }}
            className="md:hidden overflow-hidden bg-white border-t border-neutral-200">
            <div className="px-5 py-4 flex flex-col gap-1">
              {links.map((l) => <NavLink key={l.to + l.label} l={l} t={t} mobile onClick={() => setOpen(false)} />)}
              <div className="flex gap-3 mt-4">
                {user ? (
                  <>
                    {user.is_admin && (
                      <Link to="/admin" onClick={() => setOpen(false)} className="flex-1">
                        <Button variant="ghost" className="w-full border border-neutral-300 text-neutral-900 rounded-none">{t("Admin")}</Button>
                      </Link>
                    )}
                    <Link to="/dashboard" onClick={() => setOpen(false)} className="flex-1">
                      <Button className="w-full bg-[#d00000] hover:bg-[#a30000] rounded-none">{t("Dashboard")}</Button>
                    </Link>
                    <Button onClick={handleLogout} variant="ghost" className="text-neutral-900 border border-neutral-300 rounded-none">
                      <LogOut className="w-4 h-4" />
                    </Button>
                  </>
                ) : (
                  <Link to="/login" onClick={() => setOpen(false)} className="flex-1">
                    <Button className="w-full bg-[#d00000] hover:bg-[#a30000] rounded-none">{loginLabel}</Button>
                  </Link>
                )}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};
